<template>
  <div class="cached-image-container" :style="containerStyle">
    <!-- Loading placeholder with shimmer -->
    <div v-if="loading" class="image-loading-placeholder">
      <i class="el-icon-picture-outline"></i>
    </div>

    <!-- Actual image -->
    <img
      v-show="!loading && !error"
      :src="cachedSrc"
      :alt="alt"
      :class="imageClass"
      :style="imageStyle"
      @load="onImageLoad"
      @error="onImageError"
    />

    <!-- Error fallback -->
    <div v-if="error && !loading" class="image-error-placeholder">
      <img v-if="fallbackSrc" :src="fallbackSrc" :alt="alt" class="fallback-image" />
      <i v-else class="el-icon-picture-outline"></i>
    </div>
  </div>
</template>

<script>
import { getCachedImage } from '@/utils/imageCache'

export default {
  name: 'CachedImage',
  props: {
    src: {
      type: String,
      default: ''
    },
    alt: {
      type: String,
      default: ''
    },
    fallbackSrc: {
      type: String,
      default: ''
    },
    fit: {
      type: String,
      default: 'cover',
      validator: (value) => ['fill', 'contain', 'cover', 'none', 'scale-down'].includes(value)
    },
    lazy: {
      type: Boolean,
      default: true
    },
    imageClass: {
      type: String,
      default: ''
    },
    imageStyle: {
      type: [String, Object],
      default: ''
    },
    width: {
      type: [String, Number],
      default: '100%'
    },
    height: {
      type: [String, Number],
      default: '100%'
    }
  },
  data() {
    return {
      cachedSrc: '',
      loading: true,
      error: false,
      observer: null,
      isIntersecting: false
    }
  },
  computed: {
    containerStyle() {
      return {
        width: typeof this.width === 'number' ? `${this.width}px` : this.width,
        height: typeof this.height === 'number' ? `${this.height}px` : this.height
      }
    }
  },
  watch: {
    src: {
      immediate: true,
      handler(newSrc) {
        if (newSrc) {
          if (this.lazy && !this.isIntersecting) {
            // Wait for intersection
            return
          }
          this.loadImage(newSrc)
        } else {
          this.loading = false
          this.error = true
        }
      }
    }
  },
  mounted() {
    if (this.lazy) {
      this.setupIntersectionObserver()
    } else if (this.src) {
      this.loadImage(this.src)
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        // Fallback for browsers without IntersectionObserver
        this.isIntersecting = true
        if (this.src) {
          this.loadImage(this.src)
        }
        return
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isIntersecting) {
              this.isIntersecting = true
              if (this.src) {
                this.loadImage(this.src)
              }
              // Disconnect after first intersection
              this.observer.disconnect()
            }
          })
        },
        {
          rootMargin: '100px', // Start loading 100px before entering viewport
          threshold: 0
        }
      )

      this.observer.observe(this.$el)
    },
    async loadImage(url) {
      this.loading = true
      this.error = false

      try {
        // Get from cache or fetch and cache
        const cachedUrl = await getCachedImage(url)
        this.cachedSrc = cachedUrl || url
      } catch (err) {
        console.warn('CachedImage: Failed to load image:', err)
        this.cachedSrc = url // Fallback to original URL
      }
    },
    onImageLoad() {
      this.loading = false
      this.error = false
      this.$emit('load')
    },
    onImageError() {
      this.loading = false
      this.error = true
      this.$emit('error')
    }
  }
}
</script>

<style scoped>
.cached-image-container {
  position: relative;
  overflow: hidden;
  background-color: #f5f7fa;
}

.cached-image-container img {
  width: 100%;
  height: 100%;
  object-fit: v-bind(fit);
}

.image-loading-placeholder,
.image-error-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.image-loading-placeholder i,
.image-error-placeholder i {
  font-size: 32px;
  color: #c0c4cc;
}

.image-error-placeholder {
  animation: none;
  background: #f5f7fa;
}

.fallback-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style>
